import React from 'react';
import { Link } from 'react-router-dom';
import { Designer } from '../types';
import AuthenticatedImage from './AuthenticatedImage';

interface FeaturedDesignersProps {
  designers: Designer[];
  loading?: boolean;
  className?: string;
}

const FeaturedDesigners: React.FC<FeaturedDesignersProps> = ({
  designers,
  loading = false,
  className = ''
}) => {
  if (loading) {
    return (
      <div className={`bg-white py-12 px-4 ${className}`}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-center text-gray-900 mb-2">
            Featured Designers
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Discover our curated collection from renowned designers
          </p>
          
          {/* Loading skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <div className="inline-block h-12 bg-gray-200 rounded w-40 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!designers.length) {
    return (
      <div className={`bg-white py-12 px-4 ${className}`}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-gray-900 mb-2">
            Featured Designers
          </h2>
          <p className="text-gray-600">
            No featured designers available at this time.
          </p>
        </div>
      </div>
    );
  }

  // Limit to 8 designers maximum
  const displayedDesigners = designers.slice(0, 8);

  return (
    <div className={`bg-white py-12 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
            Featured Designers
          </h2>
          <p className="text-gray-600 text-lg">
            Discover our curated collection from renowned designers
          </p>
        </div>

        {/* Designers Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {displayedDesigners.map((designer) => (
            <Link
              key={designer.id}
              to={`/home/designers/${designer.id}`}
              className="block bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-[2/1] bg-gray-100 relative">
                {(designer.logoUrl || designer.logoPath) ? (
                  <AuthenticatedImage
                    src={designer.logoUrl || designer.logoPath}
                    alt={designer.name}
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-4">
                      <h3 className="text-xl font-serif text-gray-900">{designer.name}</h3>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* View All Designers Link */}
        <div className="text-center">
          <Link
            to="/home/catalog"
            className="inline-flex items-center justify-center px-6 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm"
          >
            View All Designers
          </Link>
        </div>
      </div>

    </div>
  );
};

export default FeaturedDesigners;